/**
 * OpenTelemetry-integrated Error Boundary Component.
 *
 * This component provides:
 * - User-friendly error display with trace IDs for correlation
 * - Direct client-side error reporting to OTel
 * - Copy-to-clipboard functionality for support
 */

import { trace, SpanStatusCode } from '@opentelemetry/api';
import { isRouteErrorResponse, useRouteError, Link } from '@remix-run/react';
import { Button } from 'flowbite-react';
import { useEffect, useCallback, useState } from 'react';

export interface ErrorDisplayProps {
    /** The trace ID for OTel correlation (from loader data or x-trace-id header) */
    traceId?: string;
    /** Title to show in the error message */
    title?: string;
    /** Whether to show a home button */
    showHomeButton?: boolean;
    /** Whether to show a retry button */
    showRetryButton?: boolean;
    /** Home path to navigate to */
    homePath?: string;
    /** Labels for internationalization */
    labels?: {
        title?: string;
        message?: string;
        errorId?: string;
        retry?: string;
        goHome?: string;
        copied?: string;
        copyErrorId?: string;
        notFoundTitle?: string;
        notFoundMessage?: string;
    };
}

const defaultLabels = {
    title: 'Something went wrong',
    message: 'We apologize for the inconvenience. Our team has been notified.',
    errorId: 'Error ID',
    retry: 'Try Again',
    goHome: 'Go Home',
    copied: 'Copied!',
    copyErrorId: 'Copy Error ID',
    notFoundTitle: 'Page Not Found',
    notFoundMessage: "The page you're looking for doesn't exist or has been moved."
};

/**
 * Records a client-side error directly to OTel.
 */
function recordErrorToOtel(error: Error | string, url: string): void {
    const tracer = trace.getTracer('crispyvoice-client');
    const span = tracer.startSpan('client.error', {
        attributes: {
            'error.type': 'client_render_error',
            'http.url': url
        }
    });

    const errorObj = error instanceof Error ? error : new Error(String(error));
    span.recordException(errorObj);
    span.setStatus({
        code: SpanStatusCode.ERROR,
        message: errorObj.message
    });
    span.end();
}

/**
 * Gets the current trace ID from OpenTelemetry context.
 */
function getActiveTraceId(): string | undefined {
    const activeSpan = trace.getActiveSpan();
    if (activeSpan) {
        const traceId = activeSpan.spanContext().traceId;
        // Check it's not the invalid trace ID (all zeros)
        if (traceId && traceId !== '00000000000000000000000000000000') {
            return traceId;
        }
    }
    return undefined;
}

/**
 * Generates a short error ID from the trace ID for user display.
 */
function generateErrorId(traceId?: string): string | undefined {
    if (traceId) {
        // Use first 8 chars of trace ID - enough for correlation, user-friendly
        return traceId.slice(0, 8).toUpperCase();
    }
    return undefined;
}

interface CopyButtonProps {
    text: string;
    labels: typeof defaultLabels;
}

function CopyButton({ text, labels }: CopyButtonProps) {
    const [ copied, setCopied ] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API not available
        }
    }, [ text ]);

    return (
        <button
            onClick={handleCopy}
            className='ml-2 text-xs text-gray-500 hover:text-gray-700 underline'
            title={labels.copyErrorId}
        >
            {copied ? labels.copied : '📋'}
        </button>
    );
}

/**
 * A reusable error display component that shows error information
 * and provides user-friendly recovery options.
 */
export function ErrorDisplay({
    traceId: providedTraceId,
    title,
    showHomeButton = true,
    showRetryButton = true,
    homePath = '/',
    labels: customLabels,
}: ErrorDisplayProps) {
    const labels = { ...defaultLabels, ...customLabels };
    const error = useRouteError();
    // Use provided trace ID, or try to get from active span
    const traceId = providedTraceId ?? getActiveTraceId();
    const errorId = generateErrorId(traceId);
    const isNotFound = isRouteErrorResponse(error) && error.status === 404;

    // Report error to OTel on mount
    useEffect(() => {
        if (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : isRouteErrorResponse(error)
                ? `${error.status}: ${error.statusText}`
                : 'Unknown error';

            const errorObj = error instanceof Error ? error : new Error(errorMessage);

            recordErrorToOtel(errorObj, typeof window !== 'undefined' ? window.location.href : '');
        }
    }, [ error ]);

    // 404 errors get a special treatment
    if (isNotFound) {
        return (
            <div className='min-h-screen flex items-center justify-center bg-gray-50 px-4'>
                <div className='max-w-md w-full text-center'>
                    <div className='mb-8'>
                        <h1 className='text-6xl font-bold text-gray-300 mb-4'>404</h1>
                        <h2 className='text-2xl font-semibold text-gray-800 mb-2'>{labels.notFoundTitle}</h2>
                        <p className='text-gray-600'>{labels.notFoundMessage}</p>
                    </div>

                    <div className='space-x-4'>
                        {showHomeButton && (
                            <Link to={homePath}>
                                <Button color='blue'>{labels.goHome}</Button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='min-h-screen flex items-center justify-center bg-gray-50 px-4'>
            <div className='max-w-md w-full text-center'>
                <div className='mb-8'>
                    {/* Error icon */}
                    <div className='mx-auto w-16 h-16 mb-4 text-red-500'>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={1.5}
                            stroke='currentColor'
                            className='w-full h-full'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
                            />
                        </svg>
                    </div>

                    <h1 className='text-2xl font-semibold text-gray-800 mb-2'>{title ?? labels.title}</h1>
                    <p className='text-gray-600 mb-4'>{labels.message}</p>

                    {/* Error ID for support - only show if we have a trace ID */}
                    {errorId && (
                        <div className='bg-gray-100 rounded-lg p-3 inline-block'>
                            <span className='text-sm text-gray-500'>{labels.errorId}:</span>
                            <code className='font-mono text-sm font-medium text-gray-800'>{errorId}</code>
                            <CopyButton text={traceId!} labels={labels} />
                        </div>
                    )}
                </div>

                <div className='gap-4 flex'>
                    {showRetryButton && (
                        <Button
                            color='blue'
                            onClick={() => window.location.reload()}
                        >
                            {labels.retry}
                        </Button>
                    )}
                    {showHomeButton && (
                        <Link to={homePath}>
                            <Button color='gray'>{labels.goHome}</Button>
                        </Link>
                    )}
                </div>

                {process.env.NODE_ENV === 'development' && error != null
                    ? (
                        <div className='mt-8 text-left bg-red-50 border border-red-200 rounded-lg p-4'>
                            <h3 className='font-semibold text-red-800 mb-2'>Debug Info</h3>
                            <pre className='text-xs text-red-700 whitespace-pre-wrap overflow-auto max-h-64'>
                            {error instanceof Error
                                ? `${error.message}\n\n${error.stack}`
                                : JSON.stringify(error, null, 2)}
                            </pre>
                            {traceId && (
                                <p className='mt-2 text-xs text-red-600'>
                                    Trace ID: <code className='font-mono'>{traceId}</code>
                                </p>
                            )}
                        </div>
                    )
                    : null}
            </div>
        </div>
    );
}

/**
 * A minimal error boundary for use when you don't have loader data.
 * Extracts trace context from response headers if available, or from active span.
 */
export function GenericErrorBoundary({
    labels,
    homePath = '/',
}: Pick<ErrorDisplayProps, 'labels' | 'homePath'>) {
    const [ traceId, setTraceId ] = useState<string | undefined>(() => getActiveTraceId());

    // Try to get trace context from the document's meta tag (may have more accurate server trace)
    useEffect(() => {
        const traceIdMeta = document.querySelector('meta[name="x-trace-id"]');
        if (traceIdMeta) {
            setTraceId(traceIdMeta.getAttribute('content') ?? undefined);
        }
    }, []);

    return <ErrorDisplay traceId={traceId} labels={labels} homePath={homePath} />;
}
